import { useNow } from '@/components/allocation/use-now';
import { holdClock, type HoldClock } from '@/components/allocation/types';

/**
 * The airline's grace window as a live countdown (flight-delay U5). The deadline is
 * SERVER truth (`escalation.graceUntil`, persisted by cases; brief §6 invariant 3);
 * the tick is `useNow` — a render tick, never a poll and never a timer of this module's
 * own. It ticks only while the window is still open, so a lapsed window stops
 * re-rendering; the release itself arrives over the WS (`FlightDelayChanged`).
 *
 * The clock is read at render (the tick only schedules the render), so a render caused
 * by anything else — a WS merge, a parent — never shows a stale second, and the render
 * that finds the window over is the one that stops the tick.
 *
 * Call it in the smallest leaf that shows the countdown: everything that calls it
 * re-renders once a second.
 */
export function useGraceClock(graceUntil: string | undefined): HoldClock {
  const deadline = graceUntil ? Date.parse(graceUntil) : Number.NaN;
  const now = Date.now();
  useNow(Number.isFinite(deadline) && deadline > now);
  return holdClock(graceUntil, now);
}
